import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from "react";
import { User, Desk, Reservation } from "../types";
import * as api from "../services/mockAPI";
import { useNotification } from "./NotificationContext";
import { useConfirmation } from "./ConfirmationContext";
import { useAlert } from "./AlertContext"; // Import the new hook

interface DataContextType {
  user: User | null;
  desks: Desk[];
  reservations: Reservation[];
  loading: boolean;
  addReservation: (deskId: string, date: string) => Promise<void>;
  cancelReservation: (reservationId: string) => Promise<void>;
  updateUserPreferences: (prefs: Partial<User>) => Promise<User | null>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
  currentUser: User;
}

export const DataProvider = ({ children, currentUser }: DataProviderProps) => {
  const { addNotification } = useNotification();
  const { confirm } = useConfirmation();
  const { alert } = useAlert(); // Use the new hook

  const [user, setUser] = useState<User>(currentUser);
  const [desks, setDesks] = useState<Desk[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  
  const refreshAllData = useCallback(async (userToFetchFor: User) => {
    setLoading(true);
    try {
        const [userReservations, allDesks] = await Promise.all([
            api.getReservationsForUser(userToFetchFor),
            api.getDesks(),
        ]);
        setReservations(userReservations);
        setDesks(allDesks);
        setUser(userToFetchFor);
    } catch (error) {
        console.error("Failed to fetch user data:", error);
        addNotification("Could not load user data.", "error");
    } finally {
        setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    if (currentUser) {
        refreshAllData(currentUser);
    }
  }, [currentUser, refreshAllData]);  

  const updateUserPreferences = async (newPrefs: Partial<User>): Promise<User | null> => {
    if (!user) return null;

    const updatedUser = { ...user, ...newPrefs };

    const isChangingAutoBookings = user.autoReserveEnabled && (user.defaultDeskId !== updatedUser.defaultDeskId || JSON.stringify(user.defaultWeekdays.sort()) !== JSON.stringify(updatedUser.defaultWeekdays.sort()));
    const isDisabling = user.autoReserveEnabled && !updatedUser.autoReserveEnabled;

    if (isChangingAutoBookings || isDisabling) {
      const confirmed = await confirm(
        "This will change your future automatic reservations. Manual bookings will not be affected. Do you want to continue?"
      );
      if (!confirmed) {
        addNotification("Changes discarded.", "info");
        return null;
      }
    }
    
    try {
      const savedUser = await api.updateUser(updatedUser);
      setUser(savedUser);
      await refreshAllData(savedUser);
      addNotification("Preferences saved successfully!", "success");
      return savedUser;
    } catch (error: any) {
      addNotification(error.message || "Failed to save preferences.", "error");
      return null;
    }
  };

  const addReservation = async (deskId: string, date: string) => {
    if (!user) return;
    await api.addReservation(user.id, deskId, date);
    await refreshAllData(user);
  };

  const cancelReservation = async (reservationId: string) => {
    if (!user) return;
    
    // --- THIS IS THE KEY CHANGE ---
    if (reservationId.startsWith('res-auto-')) {
        alert("Automatic reservations cannot be cancelled individually. Please disable auto-reservation in your preferences to remove them.");
        return;
    }
    // ----------------------------

    const reservationToCancel = reservations.find(r => r.id === reservationId);
    if (!reservationToCancel) return;

    await api.cancelReservation(reservationId);
    await refreshAllData(user);
    addNotification("Reservation cancelled.", "success");

    if (user.autoReserveEnabled) {
      const reservationDate = new Date(reservationToCancel.date + 'T00:00:00');
      const dayOfWeek = reservationDate.getDay();
      
      if (user.defaultWeekdays.includes(dayOfWeek)) {
        await updateUserPreferences({ ...user, autoReserveEnabled: false });
        addNotification("Auto-reservation paused due to manual cancellation of a default day.", "info");
      }
    }
  };
  
  const value = { user, desks, reservations, loading, addReservation, cancelReservation, updateUserPreferences };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) { throw new Error("useData must be used within a DataProvider"); }
    return context;
};